import { z } from "zod";
export const M365_INTEGRATION_STATUSES = [
    "disconnected",
    "consent_pending",
    "connected",
    "error",
];
/**
 * Per-agent mailbox scope status. Tracks the lifecycle of the EXO RBAC for
 * Applications role assignment that constrains the platform's app-only
 * Mail.* permissions to a single shared mailbox per agent.
 */
export const M365_MAILBOX_SCOPE_STATUSES = [
    "selected_pending_rbac",
    "rbac_verified",
    "rbac_failed",
];
/**
 * Graph API permissions requested in the admin-consent (`.default`) flow.
 *
 * Mail permissions are intentionally NOT listed here. We constrain the agent
 * to a single shared mailbox per tenant via Exchange Online RBAC for
 * Applications: the customer admin assigns `Application Mail.ReadWrite` and
 * `Application Mail.Send` roles with a `-CustomResourceScope` filter that
 * matches one mailbox alias. Tenant-wide admin consent for `Mail.*` would
 * override that scope (broadest grant wins), so we do not request it.
 *
 * `Mail.Send` and `Mail.ReadWrite` MUST still be declared as Application API
 * permissions on the Entra app registration manifest — RBAC role assignments
 * bind to declared API permissions. They simply must not be admin-consented.
 */
export const M365_CAPABILITY_SCOPES = {
    teams: [
        "Team.ReadBasic.All",
        "Channel.ReadBasic.All",
        "ChannelMessage.Send",
        "Chat.Create",
        "Chat.ReadWrite.All",
        "ChatMessage.Send",
    ],
    users: ["User.Read.All"],
    sharepoint: ["Files.ReadWrite.All", "Sites.ReadWrite.All"],
    outlook: [],
};
export const M365_CAPABILITIES = [
    "teams",
    "users",
    "sharepoint",
    "outlook",
];
/** All Graph API permissions the platform app needs (for documentation). */
export const M365_ALL_REQUIRED_PERMISSIONS = [
    ...M365_CAPABILITY_SCOPES.teams,
    ...M365_CAPABILITY_SCOPES.users,
    ...M365_CAPABILITY_SCOPES.sharepoint,
    ...M365_CAPABILITY_SCOPES.outlook,
];
export const updateM365IntegrationSchema = z.object({
    scopes: z.array(z.string().min(1).max(200)).optional(),
    enabled: z.boolean().optional(),
});
/** SharePoint export configuration stored in integration metadata. */
export const sharepointConfigSchema = z.object({
    siteId: z.string().min(1),
    siteName: z.string().min(1),
    driveId: z.string().min(1),
    driveName: z.string().min(1),
    folderPath: z.string().min(1).default("/ProClaw/Artifacts"),
});
/** Test notification request. */
export const testNotificationSchema = z.object({
    channel: z.enum(["teams_channel", "outlook"]),
    /** Incoming webhook URL for Teams channel notifications */
    webhookUrl: z.string().url().optional(),
    /** Recipient email for Outlook notifications */
    recipientEmail: z.string().email().optional(),
});
/**
 * Body of `POST /:companyId/agent/:agentId/mailbox` — admin selects which
 * shared mailbox in the customer tenant this agent will read and send from.
 * Object ID is captured at selection time and used for all Graph calls
 * (UPNs can rename; object IDs are immutable). Alias drives the EXO
 * management-scope `RecipientRestrictionFilter`.
 */
export const agentMailboxConfigSchema = z.object({
    mailboxUpn: z.string().email(),
    mailboxObjectId: z.string().min(1).max(128),
    mailboxAlias: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9._-]+$/, "alias must be ASCII letters, digits, dot, dash, underscore"),
});
//# sourceMappingURL=m365.js.map